import React, { ComponentProps, useCallback, useEffect } from 'react';
import {
  addSignCommaFormat,
  commaFormat,
} from 'components/features/new-report/utils/formatter';
import { StateController as StateControllerTypes } from './Preview';
import { RenderedChartToElementType } from './useChartRenderer';
import { GetUserAssetResult } from 'config/api';

export type ChartPreviewState = ChartPreviewStateAtDatabase & {
  totalAssetAmountValue: number;
  dayBeforeValue: number;
  dayBeforeRateValue: number;
};

type ChartPreviewStateAtDatabase = {
  totalAssetAmount: boolean;
  dayBefore: boolean;
  dayBeforeRate: boolean;
};

const isChartPreviewStateAtDatabase = (
  state: unknown
): state is ChartPreviewStateAtDatabase => {
  if (
    !(
      typeof state === 'object' &&
      state !== null &&
      'totalAssetAmount' in state &&
      'dayBefore' in state &&
      'dayBeforeRate' in state
    )
  )
    return false;
  const s = state as {
    totalAssetAmount: unknown;
    dayBefore: unknown;
    dayBeforeRate: unknown;
  };
  return (
    typeof s.totalAssetAmount === 'boolean' &&
    typeof s.dayBefore === 'boolean' &&
    typeof s.dayBeforeRate === 'boolean'
  );
};

export const ChartRange = ({
  chartRange,
}: {
  chartRange: ComponentProps<
    RenderedChartToElementType<ChartPreviewState>
  >['range'];
}) => {
  if (!chartRange) return null;
  const { max, min } = chartRange;
  const toStr = (d: Date) =>
    `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  return (
    <>
      <div className="font-bold text-xs mt-2 mr-4 ml-auto">{`${toStr(
        min
      )} ~ ${toStr(max)}`}</div>
    </>
  );
};
export const logoStyle = {
  width: '87px',
  height: '34px',
  marginTop: '0.625rem',
  marginLeft: '0.625rem',
};
export const RenderedChartToElement: RenderedChartToElementType<ChartPreviewState> = ({
  range: chartRange,
  renderedChart,
  state: { totalAssetAmount, dayBefore, dayBeforeRate, ...values },
  title,
  width,
}) => {
  return (
    <>
      <div
        className="flex flex-col bg-white"
        style={width ? { width: `${width}px` } : {}}>
        <div className="flex flex-row items-center">
          <img
            src="/static/images/minkabu_assetplanner.png"
            alt=""
            style={logoStyle}
          />
          <ChartRange {...{ chartRange }} />
        </div>
        <div className="font-bold pl-4 text-lg mb-1 mr-auto">{title}</div>
        {renderedChart}
        {(totalAssetAmount || dayBefore || dayBeforeRate) && (
          <>
            {totalAssetAmount && (
              <div className="px-2 items-baseline text-bluegray-600">
                <div className="pt-2 text-sm font-bold text-bluegray-500">
                  資産総額
                </div>
                <div className="text-base flex items-baseline flex-row">
                  <div className="font-bold text-2xl ml-auto">
                    {commaFormat(values.totalAssetAmountValue)}
                  </div>
                  <div className="ml-2 text-sm mt-2">円</div>
                </div>
              </div>
            )}
            {(dayBefore || dayBeforeRate) && (
              <div className="px-2 flex flex-row items-center">
                <div className="ml-auto mr-2 text-1.5xs">前日比</div>
                <div className="flex flex-row items-baseline">
                  {dayBefore && (
                    <div
                      className={`${
                        values.dayBeforeValue >= 0
                          ? 'text-upcolor'
                          : 'text-downcolor'
                      } flex flex-row mr-2 text-sm items-baseline`}>
                      <div className="font-bold text-sm">
                        {addSignCommaFormat(values.dayBeforeValue)}
                      </div>
                      <div className="text-1.5xs ml-1">円</div>
                    </div>
                  )}
                  {dayBeforeRate && (
                    <div
                      className={`${
                        values.dayBeforeRateValue >= 0
                          ? 'text-upcolor'
                          : 'text-downcolor'
                      } text-1.5xs`}>
                      (
                      {addSignCommaFormat(values.dayBeforeRateValue, {
                        numberOfDecimals: 2,
                      })}
                      %)
                    </div>
                  )}
                </div>
              </div>
            )}
            {(totalAssetAmount || dayBefore || dayBeforeRate) && (
              <div className="pb-4" />
            )}
          </>
        )}
      </div>
    </>
  );
};

const decodeState = (
  json: string
):
  | { tag: 'success'; state: ChartPreviewStateAtDatabase }
  | { tag: 'failure' } => {
  const obj = JSON.parse(json);
  if (isChartPreviewStateAtDatabase(obj)) {
    return { tag: 'success', state: obj };
  } else {
    return { tag: 'failure' };
  }
};

export const StateController: StateControllerTypes<ChartPreviewState> = props => {
  type Button = 'total' | 'dayBefore' | 'dayBeforeRate';
  const { setState, state } = useWithLocalStorage({
    ...props,
    localStorageKey: 'home-preview',
    mergeDatabaseTo: (state, database) => {
      return {
        ...state,
        ...database,
      };
    },
    decodeState,
    toDatabase: state => {
      return {
        dayBefore: state.dayBefore,
        dayBeforeRate: state.dayBeforeRate,
        totalAssetAmount: state.totalAssetAmount,
      };
    },
  });
  const toggle = useCallback(
    (buttonType: Button) => () => {
      setState(prev => {
        return {
          ...prev,
          totalAssetAmount:
            buttonType === 'total'
              ? !prev.totalAssetAmount
              : prev.totalAssetAmount,
          dayBefore:
            buttonType === 'dayBefore' ? !prev.dayBefore : prev.dayBefore,
          dayBeforeRate:
            buttonType === 'dayBeforeRate'
              ? !prev.dayBeforeRate
              : prev.dayBeforeRate,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const Check = ({ checked, isBig }: { checked: boolean; isBig: boolean }) => {
    return (
      <div
        className={`${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        } mr-2 font-bold rounded text-white p-1 ${
          isBig ? 'text-sm' : 'text-xs'
        } icon-check`}></div>
    );
  };
  return (
    <>
      <div className="flex flex-col gap-2 p-4">
        <div className="font-bold text-sm">表示設定</div>
        <div
          className={`p-3 rounded-sm items-center bg-bluegray-50 flex flex-row ${
            state.totalAssetAmount ? 'text-blue-600' : ''
          }`}
          onClick={toggle('total')}>
          <Check checked={state.totalAssetAmount} isBig />
          資産総額
        </div>
        <div
          className={`p-3 pl-9 text-xs rounded-sm items-center bg-bluegray-50 flex flex-row ${
            state.dayBefore ? 'text-blue-600' : ''
          }`}
          onClick={toggle('dayBefore')}>
          <Check checked={state.dayBefore} isBig={false} />
          前日比(円)
        </div>
        <div
          className={`p-3 pl-9 text-xs rounded-sm items-center bg-bluegray-50 flex flex-row ${
            state.dayBeforeRate ? 'text-blue-600' : ''
          }`}
          onClick={toggle('dayBeforeRate')}>
          <Check checked={state.dayBeforeRate} isBig={false} />
          前日比(%)
        </div>
      </div>
    </>
  );
};

export const useWithLocalStorage = <State, Database>({
  state,
  setState,
  localStorageKey,
  decodeState,
  mergeDatabaseTo,
  toDatabase,
}: {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
  localStorageKey: string;
  decodeState: (
    json: string
  ) => { tag: 'success'; state: Database } | { tag: 'failure' };
  mergeDatabaseTo: (state: State, database: Database) => State;
  toDatabase: (state: State) => Database;
}): {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
} => {
  useEffect(() => {
    const json = localStorage.getItem(localStorageKey);
    const state: ReturnType<typeof decodeState> = json
      ? decodeState(json)
      : { tag: 'failure' };
    state.tag === 'success' &&
      setState(prev => {
        return mergeDatabaseTo(prev, state.state);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const saving = toDatabase(state);
    localStorage.setItem(localStorageKey, JSON.stringify(saving));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, toDatabase]);
  return {
    state,
    setState,
  };
};

export const defaultState = ({
  dayBeforeRateValue,
  dayBeforeValue,
  totalAssetAmountValue,
}: {
  totalAssetAmountValue: number;
  dayBeforeValue: number;
  dayBeforeRateValue: number;
}): ChartPreviewState => {
  return {
    dayBefore: true,
    dayBeforeRate: true,
    totalAssetAmount: true,
    totalAssetAmountValue,
    dayBeforeValue,
    dayBeforeRateValue,
  };
};

export const defualtStateForHome = (
  asset: GetUserAssetResult
): ChartPreviewState => {
  return defaultState({
    totalAssetAmountValue: asset.TotalAssetAmount ?? 0,
    dayBeforeRateValue: asset.DiffTotalAssetRate ?? 0,
    dayBeforeValue: asset.DiffTotalAssetAmount ?? 0,
  });
};

export const stateToOptions = (
  state: ChartPreviewState
): Highcharts.Options => ({
  yAxis: {
    labels: {
      enabled: [
        state.dayBefore,
        state.dayBeforeRate,
        state.totalAssetAmount,
      ].every(b => b),
    },
  },
  scrollbar: { enabled: false },
});
